import { Box, Button, Typography } from '@mui/material';
import React from 'react';

import { importMapsLibrary } from '../utils/googleMaps';
import { placeToLegacy, PLACE_FIELDS } from './placeAdapter';

/**
 * "Fetch images from Google Places" on the activity Images tab.
 *
 * Uses `google.maps.places.Place`; `PlacesService` was marked legacy on 2025-03-01 and
 * a Cloud project created after that date cannot enable the API behind it at all.
 *
 * This used to issue TWO getDetails calls: one to fetch, and one in a useEffect keyed on
 * `[google, props]` merely to decide whether to show the button. `props` is a fresh
 * object on every parent render, so that probe re-fired constantly -- a billed Places
 * request per render. There is now a single fetch whose result answers both questions.
 */
const PlaceButton = (props) => {
    const { googleApiKey, placeId, onLoad } = props;
    const [place, setPlace] = React.useState(null);
    const [error, setError] = React.useState(false);

    React.useEffect(() => {
        if (!placeId || !googleApiKey) {
            setPlace(null);
            return undefined;
        }
        // The place id can change while a fetch is in flight (the user picks a
        // different place on the General tab); ignore anything but the last one.
        let current = true;
        setError(false);
        (async () => {
            try {
                const { Place } = await importMapsLibrary('places', googleApiKey);
                const fetched = new Place({ id: placeId });
                await fetched.fetchFields({ fields: PLACE_FIELDS });
                if (current) setPlace(fetched);
            } catch (e) {
                // A place id that no longer resolves rejects here. The old code read
                // `place.photos` off an undefined result and threw instead.
                if (current) {
                    console.warn('PlaceButton: could not load place', placeId, e);
                    setPlace(null);
                    setError(true);
                }
            }
        })();
        return () => {
            current = false;
        };
    }, [placeId, googleApiKey]);

    const hasPhotos = !!place?.photos?.length;

    const loadPlacePhotos = () => {
        if (!hasPhotos) return;
        // Adapted to the legacy shape here: ActivityEdit feeds this straight into
        // PlaceWrapper and PUTs it to the backend as place_data.
        onLoad && onLoad(placeToLegacy(place));
    };

    if (!placeId) {
        return (
            <Box my={2} mx={1}>
                <Typography variant="body2">
                    To fetch images from Google Places, please first associate this activity with a place on the
                    General Info tab.
                </Typography>
            </Box>
        );
    }

    return (
        <Box my={2}>
            {hasPhotos ? (
                <Button color="primary" onClick={loadPlacePhotos}>
                    Fetch images from Google Places
                </Button>
            ) : (
                <Box mx={1}>
                    <Typography variant="body2">
                        {error
                            ? 'Could not reach Google Places for this activity. Please upload images manually.'
                            : 'No images available. Please upload manually.'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default PlaceButton;
